import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Minus, Info } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

interface ScheduledMatchLite {
  id: string;
  leagueId?: string | null;
  status?:
    | "scheduled"
    | "score_pending"
    | "score_submitted"
    | "completed"
    | "cancelled";
  score?: { sets?: Array<{ team1: number; team2: number }> } | null;
  winner?: 1 | 2 | null;
  team1Player1?: { name: string } | null;
  team1Player2?: { name: string } | null;
  team2Player1?: { name: string } | null;
  team2Player2?: { name: string } | null;
}

interface Props {
  match: ScheduledMatchLite | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const MAX_SETS = 5;
const MAX_GAMES = 7;
// Same preset score grid used by EditPendingMatchDialog and the main
// "Registrar Partido" form, so all score-entry surfaces look and feel
// the same.
const COMMON_SCORES = [0, 1, 2, 3, 4, 5, 6, 7];
const QUICK_RESULTS: ReadonlyArray<{ team1: number; team2: number }> = [
  { team1: 6, team2: 0 },
  { team1: 6, team2: 2 },
  { team1: 6, team2: 4 },
  { team1: 7, team2: 5 },
  { team1: 7, team2: 6 },
];

function autoFillOther(picked: number, currentOther: number): number {
  if (currentOther !== 0) return currentOther;
  if (picked >= 0 && picked <= 4) return 6;
  if (picked === 5) return 7;
  if (picked === 7) return 5;
  return currentOther;
}

/**
 * Lets a league admin/owner record the final result of a scheduled match
 * without waiting for the opposing team to confirm. Posts to the
 * `POST /api/scheduled-matches/:id/admin-result` endpoint and invalidates
 * the related caches.
 */
export function RegisterScheduledMatchResultDialog({
  match,
  onClose,
  onSuccess,
}: Props) {
  const { toast } = useToast();
  const isOverride =
    match?.status === "completed" || match?.status === "score_submitted";
  const [sets, setSets] = useState<Array<{ team1: number; team2: number }>>([
    { team1: 0, team2: 0 },
  ]);

  useEffect(() => {
    if (!match) return;
    const existing = match.score?.sets;
    if (Array.isArray(existing) && existing.length > 0) {
      setSets(
        existing
          .filter(
            (s): s is { team1: number; team2: number } =>
              !!s &&
              typeof s.team1 === "number" &&
              typeof s.team2 === "number",
          )
          .map((s) => ({ team1: s.team1, team2: s.team2 })),
      );
    } else {
      setSets([{ team1: 0, team2: 0 }]);
    }
  }, [match?.id]);

  const allValid = sets.every(
    (s) =>
      Number.isFinite(s.team1) &&
      Number.isFinite(s.team2) &&
      s.team1 >= 0 &&
      s.team2 >= 0,
  );
  const team1Wins = allValid
    ? sets.filter((s) => s.team1 > s.team2).length
    : 0;
  const team2Wins = allValid
    ? sets.filter((s) => s.team2 > s.team1).length
    : 0;
  const setsWithWinner = allValid
    ? sets.filter((s) => s.team1 !== s.team2).length
    : 0;
  const allSetsResolved = allValid && setsWithWinner === sets.length;

  const winner = useMemo<1 | 2 | null>(() => {
    if (!allValid) return null;
    if (setsWithWinner === 0) return null;
    if (team1Wins > team2Wins) return 1;
    if (team2Wins > team1Wins) return 2;
    return 1;
  }, [allValid, setsWithWinner, team1Wins, team2Wins]);

  const team1Names =
    [match?.team1Player1?.name, match?.team1Player2?.name]
      .filter(Boolean)
      .join(" / ") || "Equipo 1";
  const team2Names =
    [match?.team2Player1?.name, match?.team2Player2?.name]
      .filter(Boolean)
      .join(" / ") || "Equipo 2";

  // ---- Live result preview ----------------------------------------
  // Mirrors the "Editar marcador" sheet (mobile + EditPendingMatchDialog)
  // and the main "Registrar Partido" form so the admin sees the same
  // immediate feedback (set tally + who's currently winning) instead of
  // just the plain "Ganador: …" line. Real names are used when available.
  const resultPreview = useMemo<{
    text: string;
    tone: "muted" | "team1" | "team2";
  }>(() => {
    if (!allValid) {
      return {
        text: "Ingresa los puntajes para ver el resultado",
        tone: "muted",
      };
    }
    if (setsWithWinner === 0) {
      return { text: "Sin definir aún", tone: "muted" };
    }
    if (team1Wins === team2Wins) {
      return { text: `Empate ${team1Wins}-${team2Wins}`, tone: "muted" };
    }
    const leadingTeam: 1 | 2 = team1Wins > team2Wins ? 1 : 2;
    const lead = Math.max(team1Wins, team2Wins);
    const trail = Math.min(team1Wins, team2Wins);
    const leaderName = leadingTeam === 1 ? team1Names : team2Names;
    return {
      text: `${leaderName} ${allSetsResolved ? "gana" : "va ganando"} ${lead}-${trail}`,
      tone: leadingTeam === 1 ? "team1" : "team2",
    };
  }, [
    allValid,
    setsWithWinner,
    team1Wins,
    team2Wins,
    team1Names,
    team2Names,
    allSetsResolved,
  ]);

  const updateSet = (idx: number, team: "team1" | "team2", value: number) => {
    const next = [...sets];
    const clamped = Math.max(
      0,
      Math.min(MAX_GAMES, Number.isFinite(value) ? value : 0),
    );
    const other: "team1" | "team2" = team === "team1" ? "team2" : "team1";
    next[idx] = {
      ...next[idx],
      [team]: clamped,
      [other]: autoFillOther(clamped, next[idx][other]),
    };
    setSets(next);
  };

  const applyQuickResult = (idx: number, team1: number, team2: number) => {
    const next = [...sets];
    next[idx] = { team1, team2 };
    setSets(next);
  };

  const addSet = () => {
    if (sets.length < MAX_SETS) {
      setSets([...sets, { team1: 0, team2: 0 }]);
    }
  };

  const removeSet = () => {
    if (sets.length > 1) {
      setSets(sets.slice(0, -1));
    }
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!match) throw new Error("No hay partido");
      if (sets.some((s) => s.team1 === s.team2)) {
        throw new Error("Cada set debe tener un ganador (sin empates).");
      }
      if (!winner) throw new Error("Ingresa al menos un set válido");
      return apiRequest(
        "POST",
        `/api/scheduled-matches/${match.id}/admin-result`,
        { score: { sets }, winner },
      );
    },
    onSuccess: async (response) => {
      if (response.ok) {
        toast({
          title: isOverride
            ? "Resultado corregido"
            : "Resultado registrado",
          description: "Los participantes recibirán una notificación.",
        });
        if (match?.leagueId) {
          queryClient.invalidateQueries({
            queryKey: ["/api/leagues", match.leagueId],
          });
        }
        queryClient.invalidateQueries({ queryKey: ["/api/scheduled-matches"] });
        queryClient.invalidateQueries({
          queryKey: ["/api/scheduled-matches/upcoming"],
        });
        queryClient.invalidateQueries({
          queryKey: ["/api/profile/match-history"],
        });
        queryClient.invalidateQueries({
          queryKey: ["/api/profile/level-history"],
        });
        queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
        queryClient.invalidateQueries({
          queryKey: ["/api/notifications/unread-count"],
        });
        onSuccess?.();
      } else {
        const err = await response.json().catch(() => ({}));
        toast({
          title: "Error",
          description: err?.message || "No se pudo registrar el resultado",
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo registrar el resultado",
        variant: "destructive",
      });
    },
  });

  const saving = submitMutation.isPending;

  return (
    <Dialog
      open={!!match}
      onOpenChange={(open) => {
        if (!open && !saving) onClose();
      }}
    >
      <DialogContent
        className="max-w-md"
        data-testid="dialog-register-scheduled-result"
      >
        <DialogHeader>
          <DialogTitle>
            {isOverride ? "Corregir resultado" : "Registrar resultado"}
          </DialogTitle>
          <DialogDescription>
            {isOverride
              ? "Vas a sobrescribir el resultado actual del partido. Las estadísticas se actualizarán y los 4 jugadores recibirán una notificación."
              : "Como administrador de la liga, el resultado se guardará directamente sin requerir aprobación del rival."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-start gap-2 rounded-md border border-primary/40 bg-primary/5 p-3 text-xs">
            <Info className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p>
                <span className="font-medium">Equipo 1:</span> {team1Names}
              </p>
              <p>
                <span className="font-medium">Equipo 2:</span> {team2Names}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {sets.map((set, idx) => (
              <div key={idx} className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs text-muted-foreground">
                    Set {idx + 1}
                  </Label>
                  <div className="flex flex-wrap items-center gap-1">
                    {QUICK_RESULTS.map((r) => {
                      const active =
                        set.team1 === r.team1 && set.team2 === r.team2;
                      return (
                        <Button
                          key={`${r.team1}-${r.team2}`}
                          type="button"
                          size="sm"
                          variant={active ? "default" : "outline"}
                          className="h-7 px-2 text-xs"
                          onClick={() =>
                            applyQuickResult(idx, r.team1, r.team2)
                          }
                          data-testid={`button-register-quick-${idx}-${r.team1}-${r.team2}`}
                        >
                          {r.team1}-{r.team2}
                        </Button>
                      );
                    })}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs"
                      onClick={() =>
                        applyQuickResult(idx, set.team2, set.team1)
                      }
                      data-testid={`button-register-invert-${idx}`}
                    >
                      Invertir
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="grid grid-cols-4 gap-1"
                    data-testid={`input-register-set-${idx}-team1`}
                  >
                    {COMMON_SCORES.map((score) => (
                      <Button
                        key={score}
                        type="button"
                        size="sm"
                        variant={set.team1 === score ? "default" : "outline"}
                        className="h-8 w-8 p-0 text-xs"
                        onClick={() => updateSet(idx, "team1", score)}
                        data-testid={`button-register-set-${idx}-team1-${score}`}
                      >
                        {score}
                      </Button>
                    ))}
                  </div>
                  <span className="text-muted-foreground">-</span>
                  <div
                    className="grid grid-cols-4 gap-1"
                    data-testid={`input-register-set-${idx}-team2`}
                  >
                    {COMMON_SCORES.map((score) => (
                      <Button
                        key={score}
                        type="button"
                        size="sm"
                        variant={set.team2 === score ? "default" : "outline"}
                        className="h-8 w-8 p-0 text-xs"
                        onClick={() => updateSet(idx, "team2", score)}
                        data-testid={`button-register-set-${idx}-team2-${score}`}
                      >
                        {score}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            {sets.length < MAX_SETS && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addSet}
                data-testid="button-register-add-set"
              >
                <Plus className="h-3 w-3 mr-1" />
                Set
              </Button>
            )}
            {sets.length > 1 && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={removeSet}
                data-testid="button-register-remove-set"
              >
                <Minus className="h-3 w-3 mr-1" />
                Set
              </Button>
            )}
          </div>

          <div className="space-y-1 text-center">
            <p
              className={
                "text-sm font-medium " +
                (resultPreview.tone === "team1"
                  ? "text-primary"
                  : resultPreview.tone === "team2"
                    ? "text-destructive"
                    : "text-muted-foreground")
              }
              data-testid="text-register-winner"
            >
              {resultPreview.text}
            </p>
            {allValid && team1Wins === team2Wins && setsWithWinner > 0 ? (
              <p
                className="text-xs text-muted-foreground"
                data-testid="text-register-winner-hint"
              >
                Empate en sets — el ganador se decide por juegos totales.
              </p>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            data-testid="button-register-cancel"
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              // Optimistic close: snap the dialog shut so the
              // save feels instant. The mutation continues in the
              // background; any error toast still fires from
              // onError.
              submitMutation.mutate();
              onClose();
            }}
            disabled={saving || setsWithWinner === 0}
            data-testid="button-register-submit"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {isOverride ? "Guardar corrección" : "Guardar resultado"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
